import { useEffect } from "react";
import {
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";

import { authRepository } from "@/infrastructure/auth/auth.repository";
import { SidebarInset, SidebarProvider } from "@monorepo-apps/ui/src/components/sidebar";
import { AppSidebar } from "@/presentation/components/shell/app-sidebar";
import { PipelineRibbon } from "@/presentation/components/shell/pipeline-ribbon";
import { useWorkspace } from "@/presentation/providers/workspace.provider";
import { routeWorkspace, workspaceHome } from "@/presentation/data/sidebar-items";

export const Route = createFileRoute("/_app")({
  beforeLoad: async () => {
    const session = await authRepository.getSession();
    if (!session) {
      throw redirect({ to: "/login" });
    }
  },
  component: AppLayout,
});

/**
 * Keeps the active route in sync with workspace access (nav-visibility boundary):
 * - a route the user has no access to → redirect to their current workspace home
 * - a route in an allowed-but-not-current workspace → switch to it (deep links)
 */
function WorkspaceRouteGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentWorkspace, availableWorkspaces, setWorkspace, loading } = useWorkspace();

  useEffect(() => {
    if (loading) return;
    // Fail-closed users (resolved profile, no workspaces) have nowhere to be
    // redirected to — don't loop; the sidebar simply renders empty.
    if (availableWorkspaces.length === 0) return;
    const target = routeWorkspace(location.pathname);
    if (!target) return;
    if (!availableWorkspaces.includes(target)) {
      void navigate({ to: workspaceHome(currentWorkspace), replace: true });
      return;
    }
    if (target !== currentWorkspace) {
      setWorkspace(target);
    }
  }, [location.pathname, availableWorkspaces, currentWorkspace, loading, navigate, setWorkspace]);

  return null;
}

function AppLayout() {
  return (
    <SidebarProvider>
      <WorkspaceRouteGuard />
      <AppSidebar />
      <SidebarInset className="bg-background min-w-0">
        <PipelineRibbon />
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}
